import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef
} from "react";
import { UppyFile } from "@uppy/core";
import { Dashboard as UppyDashboard, useUppyEvent } from "@uppy/react";
import { UppyStruct, FileMeta, FileBody, createUppy } from "./uppy";
import { gssCreatePhoto, generateS3Key } from "./services";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import "@uppy/webcam/dist/style.min.css";

async function onFilesAdded(
  uppy: UppyStruct,
  files: UppyFile<FileMeta, FileBody>[]
) {
  files.forEach(file => {
    const s3Key = generateS3Key(file);
    if (!s3Key) {
      console.error("s3Key not generated for file ", file.name);
      return;
    }
    uppy.setFileMeta(file.id, { s3Key });
  });
}

function onFileAdded(uppy: UppyStruct, file: UppyFile<FileMeta, FileBody>) {
  if (file.source === "Webcam") {
    // Show the Webcam panel again
    const dashboard = uppy.getPlugin("Dashboard") as any;
    if (dashboard && typeof dashboard.showPanel === "function") {
      setTimeout(() => {
        dashboard.showPanel("Webcam");
      }, 1000);
    }
  }
}

async function onUploadSuccess(
  _uppy: any,
  file: UppyFile<FileMeta, FileBody>,
  response: unknown
): Promise<number> {
  // TODO: batching
  // - add the file to a batch
  // - call gssCreatePhoto when the batch (of 10) is full
  // - call gssCreatePhoto also when 4 seconds have passed since last clearance
  // - invoke the onFinish callback for each file
  const result = await gssCreatePhoto(file, file.meta.albumId as number);
  const gssPhoto = result.photo;
  return gssPhoto.id as number;
}

type UppyProps = {
  albumId: number;
  onUploadComplete?: () => void;
  hasVideoAccess: boolean;
  closeUploader: () => void;
  onDuplicateDecision: (duplicates: string[]) => Promise<boolean>;
  mobileCameraOnlyUpload?: boolean;
};

export interface UppyDashboardRef {
  pause: () => void;
  resume: () => void;
  isUploadInProgress: () => boolean;
  hasFilesAdded: () => boolean;
  filesNotUploaded: () => boolean;
}

export const EnhancedUploaderDashboard = forwardRef<
  UppyDashboardRef,
  UppyProps
>((props, ref) => {
  // IMPORTANT: passing an initializer function to prevent Uppy from being
  // reinstantiated on every render.
  const [uppy] = useState(() =>
    createUppy({
      albumId: props.albumId,
      hasVideoAccess: props.hasVideoAccess,
      onDuplicateDecision: props.onDuplicateDecision,
      mobileCameraOnlyUpload: props.mobileCameraOnlyUpload
    })
  );
  const [webcamWasActive, setWebcamWasActive] = useState(false);
  const webcamWasActiveRef = useRef(false);
  const getUploadState = () => {
    const state = uppy.getState();
    return Object.values(state.files).some(
      file => file.progress?.uploadStarted && !file.progress?.uploadComplete
    );
  };
  const hasFilesAdded = () => {
    const { files } = uppy.getState();
    return Object.keys(files).length > 0;
  };

  const filesNotUploaded = () => {
    const { files } = uppy.getState();
    return Object.values(files).some(file => !file.progress?.uploadComplete);
  };

  // Added this hook to expose methods via ref so that we could use uppy instance in parent component
  useImperativeHandle(
    ref,
    () => ({
      pause: () => {
        uppy.pauseAll();
      },
      resume: () => {
        uppy.resumeAll();
      },
      isUploadInProgress: getUploadState,
      hasFilesAdded: hasFilesAdded,
      filesNotUploaded: filesNotUploaded
    }),
    [uppy]
  );

  // Check if we're on mobile and restrictions are enabled
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const shouldRestrictToCamera = props.mobileCameraOnlyUpload && isMobile;
  
  const dashboardProps = {
    uppy: uppy,
    plugins: shouldRestrictToCamera ? ["Webcam"] : ["GoogleDrive", "Webcam"],
    inline: true,
    doneButtonHandler: () =>
      props.closeUploader ? props.closeUploader() : null,
    width: "100%"
  };

  useUppyEvent(uppy, "complete", () => {
    if (props.onUploadComplete) {
      props.onUploadComplete();
    }
  });

  useUppyEvent(uppy, "files-added", files => {
    onFilesAdded(uppy, files);
  });

  useUppyEvent(uppy, "file-added", file => {
    onFileAdded(uppy, file);
  });

  useUppyEvent(uppy, "file-removed", fileID => {
    const stillUploading = uppy
      .getFiles()
      .some(
        file => file.progress?.uploadStarted && !file.progress?.uploadComplete
      );
  });

  useUppyEvent(uppy, "upload-success", async (file, response) => {
    if (file === undefined) {
      console.error("Upload finished for unknown file");
      return;
    } else {
      // use uppy.addPostProcessor instead
      try {
        await onUploadSuccess(uppy, file, response);
      } catch (err) {
        console.error("Failed to create photo record", err);
        uppy.info("Photo uploaded but saving failed. Please retry.", "error");
      }
    }
  });

  // Note: this can also be used instead of the "upload-success" event
  // uppy.addPostProcessor((args)=>{
  //   console.log("addPostProcessor", args);
  //   Promise.resolve(101);
  // })

  useEffect(() => {
    return () => {
      const state = uppy.getState();
      const isUploadInProgress = Object.values(state.files).some(file => {
        const progress = file.progress;
        return progress?.uploadStarted && !progress?.uploadComplete;
      });
      if (isUploadInProgress) {
        uppy.cancelAll();
      }
    };
  }, [uppy]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      const webcam = uppy.getPlugin("Webcam") as any;
      const dashboard = uppy.getPlugin("Dashboard") as any;

      if (!webcam || !dashboard) return;

      if (document.hidden) {
        // Track if webcam was active before stopping it
        const wasActive = !!webcam.stream;
        setWebcamWasActive(wasActive);
        webcamWasActiveRef.current = wasActive;

        // Stop the webcam stream if it's running
        if (webcam.stream) {
          webcam.stream.getTracks().forEach((track: MediaStreamTrack) => {
            track.stop();
          });
        }
        webcam.stream = null;
      } else {
        // Only restart webcam if it was previously active
        if (webcamWasActiveRef.current) {
          try {
            await webcam.start();
            setWebcamWasActive(false); // Reset the flag
            webcamWasActiveRef.current = false;
          } catch (err) {
            console.error("Failed to restart webcam:", err);
            uppy.info(
              "Camera access denied. Please check permissions.",
              "error"
            );
            setWebcamWasActive(false);
            webcamWasActiveRef.current = false;
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [uppy]);

  return (
    <div style={{ position: "relative" }}>
      <UppyDashboard
        uppy={uppy}
        plugins={shouldRestrictToCamera ? ["Webcam"] : ["GoogleDrive", "Webcam"]}
        inline={true}
        doneButtonHandler={() =>
          props.closeUploader ? props.closeUploader() : null
        }
        width="100%"
        disableLocalFiles={shouldRestrictToCamera}
        locale={{
          //@ts-ignore
          strings: {
            cancel: "Close" // Only override cancel here
          }
        }}
      />
    </div>
  );
});
